import { io } from "socket.io-client";
import appConfig from "configs/app.config";
import { simpleNotify } from "components/custom/SimpleNotify";

let socket;
// Store active poll notification IDs
let activePollNotifications = {};

const registerSocket = (userId) => {
  if (socket) return socket;

  socket = io(`${appConfig.apiBaseUrl}`, {
    reconnectionDelayMax: 10000,
    query: {
      userId: userId,
    },
    transports: ["websocket"], // Force WebSocket transport
    reconnection: true, // Enable reconnection
    reconnectionAttempts: 5, // Try to reconnect 5 times
    reconnectionDelay: 1000, // Wait 1 second before retrying
  });

  // Connection event handlers
  socket.on("connect", () => {
    console.log("Socket connected with ID:", socket.id);
  });

  socket.on("connect_error", (error) => {
    console.error("Socket connection error:", error);
  });

  socket.on("disconnect", (reason) => {
    console.log("Socket disconnected:", reason);
  });

  // Handle socket connection confirmation
  socket.on("socketConnected", (data) => {
    console.log("Socket connection confirmed:", data);
  });

  // Handle progress updates
  socket.on("bulkDownloadProgress", (data) => {
    console.log("Progress update:", data);
    // You can add your progress handling logic here
    // For example, updating a progress bar or showing status messages
  });

  // Handle poll checker status updates with custom notification
  socket.on("pollCheckerStatus", (data) => {
    console.log("Poll checker status update:", data);

    const pollId = data.pollId || data.id || "default";
    const isComplete = ["completed", "failed"].includes(data.status);

    // DEBUG: Add a console log to verify the notification is being triggered
    console.log("Creating notification with data:", {
      pollId,
      status: data.status,
      title: data.title,
      message: data.message,
      details: data.details,
    });

    // If we already have a notification for this poll, update it
    if (activePollNotifications[pollId]) {
      simpleNotify.update(activePollNotifications[pollId], {
        status: data.status,
        title: data.title,
        message:
          data.message ||
          `Poll checker ${data.status}${
            data.details ? `: ${data.details}` : ""
          }`,
      });

      // For completed status, we let the component handle auto-removal
      // Just clean up our reference after a delay
      if (isComplete) {
        setTimeout(() => {
          delete activePollNotifications[pollId];
        }, 5000); // Match the auto-removal delay
      }
    }
    // Otherwise create a new notification
    else {
      const notificationId = simpleNotify.show({
        status: data.status,
        title: data.title,
        message:
          data.message ||
          `Poll checker ${data.status}${
            data.details ? `: ${data.details}` : ""
          }`,
      });

      // Store reference
      activePollNotifications[pollId] = notificationId;

      // For completed status, we let the component handle auto-removal
      // Just clean up our reference after a delay
      if (isComplete) {
        setTimeout(() => {
          delete activePollNotifications[pollId];
        }, 5000); // Match the auto-removal delay
      }
    }
  });

  // Handle zip file downloads
  socket.on("zip", (data) => {
    console.log("Received zip data:", data);
    if (!Array.isArray(data) || data.length === 0) {
      console.error("Invalid zip data received");
      return;
    }

    const fileUrls = data.map((download) => ({
      link: download?.url,
      name: download?.name,
    }));

    // Start download after a short delay
    setTimeout(async () => {
      for (const url of fileUrls) {
        try {
          const response = await fetch(url.link);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");

          link.href = downloadUrl;
          link.setAttribute("download", `${url.name}`);
          link.download = url.name;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          window.URL.revokeObjectURL(downloadUrl);
          document.body.removeChild(link);

          console.log(`Successfully downloaded: ${url.name}`);
        } catch (error) {
          console.error("Error downloading file:", error);
        }
      }
    }, 1000); // Reduced delay to 1 second
  });

  // Handle errors
  socket.on("socketErr", (error) => {
    console.error("Socket error:", error);
  });

  return socket;
};

const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    console.log("Socket disconnected and cleaned up");
  }
  // Clear any active notifications
  Object.keys(activePollNotifications).forEach((id) => {
    simpleNotify.remove(activePollNotifications[id]);
  });
  activePollNotifications = {};
};

const getSocket = () => socket;

// Export for testing
export const testNotification = () => {
  // Simulate a poll checker status update for testing
  if (socket) {
    console.log("Simulating poll checker status update for testing...");
    // First send a "started" status
    const pollData = {
      pollId: "test-poll-" + Date.now(),
      status: "started",
      title: "Test Poll Started",
      message: "This is a test poll that has just started",
    };

    socket.emit("pollCheckerStatus", pollData);

    // After 3 seconds, send a "completed" status
    setTimeout(() => {
      const completedData = {
        ...pollData,
        status: "completed",
        title: "Test Poll Completed",
        message: "This test poll has completed successfully!",
      };
      socket.emit("pollCheckerStatus", completedData);
    }, 3000);

    return true;
  }
  return false;
};

// Export for direct notification testing (without socket)
export const testNotifyDirect = () => {
  return simpleNotify.test();
};

export { registerSocket, getSocket, disconnectSocket };
